import {STATE_VALUE_WILDCARD_SUFFIX} from '@/constants/engine';
import {ReferenceQuery, ValueFilter, DateFilter, PropertyFilter, ArrayFilter, ConditionFilter} from '@/model/types';
import {DOMAIN_VALUE_PREFIX} from '@/model/types/constants';
import {getPredicate} from '../predicates';
import {DocumentData, DocumentValue, ValueResolver, ValueResolverOptions, VisibilitySet} from '../types';
import {recursiveReplaceIndex} from '../utils/object';
import {isReferenceAlwaysVisible, isFieldVisible} from '../utils/reference';
import {sortDocumentValues} from '../utils/sort';

export function resolveQuery<T>(
  reference: ReferenceQuery,
  data: DocumentData['data'],
  resolver: ValueResolver,
  options: ValueResolverOptions<T>,
): T {
  if (reference.groupBy) {
    return resolveReferenceQueryGrouped({...reference, groupBy: reference.groupBy}, data, resolver, options);
  } else {
    return resolveReferenceQuery(reference, data, resolver, options);
  }
}

function expandQuery(
  query: string,
  data: DocumentData['data'],
  visibilitySet?: VisibilitySet,
): [string, DocumentValue | DocumentValue[]][] {
  return Object.entries(data)
    .filter(([key]) => key.startsWith(query.slice(0, -STATE_VALUE_WILDCARD_SUFFIX.length)))
    .filter(([key]) => isReferenceAlwaysVisible(key) || isFieldVisible(key, visibilitySet));
}

function applyArrayFilterToRow<T>(
  filter: ArrayFilter,
  rowKey: string,
  resolver: ValueResolver,
  options: ValueResolverOptions<T>,
): boolean {
  const rowIndex = rowKey.split('#')[1];
  const {field, value} = recursiveReplaceIndex(filter, rowIndex ?? '');

  const fieldValue = resolver({$ref: field}, {visibilitySet: options.visibilitySet});
  const targetValue = resolver(value, {visibilitySet: options.visibilitySet});

  switch (filter.operator) {
    case '=':
      return fieldValue === targetValue;
    case '!=':
      return fieldValue !== targetValue;
    case 'in':
      return targetValue.includes(fieldValue);
    case 'not in':
      return !targetValue.includes(fieldValue);
  }
}

function applyConditionFilterToRow<T>(
  filter: ConditionFilter,
  rowKey: string,
  resolver: ValueResolver,
  options: ValueResolverOptions<T>,
  includeHiddenFields?: boolean,
): boolean {
  const rowIndex = rowKey.split('#')[1] ?? '';
  const condition = recursiveReplaceIndex(filter, rowIndex);

  const visibilitySet = includeHiddenFields ? undefined : options.visibilitySet;

  return getPredicate(condition)(
    (value, valueOptions) => resolver(value, {...valueOptions, visibilitySet}),
    visibilitySet ?? new Set(),
  )();
}

function applyFiltersToQueryKeys<T>(
  queryKeys: [string, DocumentValue | DocumentValue[]][],
  filters: (ValueFilter | DateFilter | PropertyFilter | ArrayFilter | ConditionFilter)[],
  resolver: ValueResolver,
  options: ValueResolverOptions<T>,
  includeHiddenFields?: boolean,
): [string, DocumentValue | DocumentValue[]][] {
  if (!filters || filters.length === 0) {
    return queryKeys;
  }

  return queryKeys.filter(([key, value]) => {
    return filters.every(filter => {
      if (filter.operator === 'and' || filter.operator === 'or' || filter.operator === 'not') {
        return applyConditionFilterToRow(filter, key, resolver, options, includeHiddenFields);
      }

      if ('field' in filter && (filter.operator === '=' || filter.operator === '!=')) {
        return applyArrayFilterToRow(filter, key, resolver, options);
      }

      switch (filter.operator) {
        case 'in':
        case 'not in': {
          if ('field' in filter) {
            return applyArrayFilterToRow(filter, key, resolver, options);
          }
          if (filter.value == null) return true;
          if (value == null) return false;
          const resolvedValue = resolver(value);
          const match = (Array.isArray(filter.value) ? filter.value : [])
            .map(v => resolver({$ref: v}))
            .includes(resolvedValue);

          return filter.operator === 'in' ? match : !match;
        }

        case 'has':
        case 'not has': {
          if (filter.field == null) return true;
          if (value === null) return false;
          const resolvedField = resolver(filter.field);
          const resolvedValue = resolver<string[]>({
            $ref: resolver<string>(value, {keepDomainReference: true}),
            property: filter.property,
          });

          const match = resolvedValue.includes(resolvedField);

          return filter.operator === 'has' ? match : !match;
        }

        case 'date': {
          const resolvedValue = resolver(value);
          const dateValue = new Date(resolvedValue);
          if (isNaN(dateValue.getTime())) return false;

          if (filter.value.min && dateValue < new Date(filter.value.min)) return false;
          if (filter.value.max && dateValue > new Date(filter.value.max)) return false;
          return true;
        }

        default:
          return true;
      }
    });
  });
}

function resolveQueryResult<T>(
  value: DocumentValue | DocumentValue[],
  resolver: ValueResolver,
  options: ValueResolverOptions<T>,
  property?: ReferenceQuery['property'],
) {
  if (typeof value === 'string' && value.startsWith(DOMAIN_VALUE_PREFIX)) {
    return resolver(
      {$ref: value, ...(property ? {property} : {})},
      {keepReference: options.keepReference, keepDomainReference: options.keepDomainReference},
    );
  }

  if (typeof value === 'object' && '$ref' in value) {
    return resolver(
      {...value, ...(property ? {property} : {})},
      {keepReference: options.keepReference, keepDomainReference: options.keepDomainReference},
    );
  }

  return resolver(value, {keepReference: options.keepReference, keepDomainReference: options.keepDomainReference});
}

function getIntersectionResults(values: string[]): string[] {
  const intersection = values.reduce(
    (intersectionSet, currentSet) => intersectionSet.intersection(new Set<string>(currentSet)),
    new Set<string>(values[0]),
  );

  return Array.from(intersection);
}

function resolveReferenceQuery<T>(
  reference: ReferenceQuery,
  data: DocumentData['data'],
  resolver: ValueResolver,
  options: ValueResolverOptions<T>,
): T {
  const {$query, transform, sort, resolve = true, filters, intersect, includeHiddenFields} = reference;

  const queryKeys = applyFiltersToQueryKeys(
    expandQuery($query, data, includeHiddenFields ? undefined : options.visibilitySet),
    filters || [],
    resolver,
    options,
    includeHiddenFields,
  );

  if (transform === 'count') {
    return queryKeys.length as T;
  }

  if (!resolve) {
    return queryKeys.map(([_, value]) => value).sort(sortDocumentValues(sort)) as T;
  }

  const result = queryKeys
    .map(([_, value]) => resolveQueryResult<T>(value, resolver, options, reference.property))
    .sort(sortDocumentValues(sort));

  if (intersect) {
    return getIntersectionResults(result) as T;
  }

  return result as T;
}

function resolveReferenceQueryGrouped<T>(
  reference: ReferenceQuery & {groupBy: string},
  data: DocumentData['data'],
  resolver: ValueResolver,
  options: ValueResolverOptions<T>,
): T {
  const {$query, groupBy, transform, sort, distinct, resolve = true, filters, includeHiddenFields} = reference;

  // Find all the state keys that matches the query
  const queryKeys = applyFiltersToQueryKeys(
    expandQuery($query, data, includeHiddenFields ? undefined : options.visibilitySet),
    filters || [],
    resolver,
    options,
    includeHiddenFields,
  );

  // Prefill state keys (INVE*, COMP*, ...) are not in the visibility set, so grouping on them would discard every row.
  const groupByVisibilitySet = includeHiddenFields ? undefined : options.visibilitySet;
  // Find all the state keys that match the groupBy value (usually array rows).
  const groupByKeys = Object.entries(data)
    .filter(([key]) => key.startsWith(groupBy))
    .filter(([key]) => isReferenceAlwaysVisible(key) || isFieldVisible(key, groupByVisibilitySet))
    .map(([key]) => ({key, value: resolver(data[key])}));

  const res: Record<string, {key: string; value: string}[]> = {};

  // For each query result, take its row index
  for (const [key, value] of queryKeys) {
    const rowIndex = key.split('#')[1];
    // Find the groupBy field corresponding to that row
    const group = groupByKeys.find(({key}) => key.endsWith(`#${rowIndex}`));
    if (!group) continue;

    // Put in the grouped object at the groupBy field's value
    res[group.value] = res[group.value] ?? [];
    const serialized = JSON.stringify(value);
    if (distinct && res[group.value].find(item => item.key === serialized)) continue;
    res[group.value].push({key: serialized, value: JSON.stringify(resolve ? value : key)});
  }

  if (transform === 'count') {
    return Object.keys(res).length as T;
  }

  if (!resolve) {
    return Object.values(res).map(v =>
      v
        .map(({value}) => value)
        .sort(sortDocumentValues(sort))
        .map(value => JSON.parse(value)),
    ) as T;
  }

  return Object.values(res).map(v => {
    return v
      .map(({value}) =>
        resolver(JSON.parse(value), {
          keepReference: options.keepReference,
          keepDomainReference: options.keepDomainReference,
        }),
      )
      .sort(sortDocumentValues(sort));
  }) as T;
}
